import { useEffect, useState, useMemo } from 'react';
import api from '../lib/api';
import Button from './ui/Button';
import Input from './ui/Input';
import Textarea from './ui/Textarea';
import { Card, CardContent, CardHeader, CardTitle } from './ui/Card';
import Select from './ui/Select';

function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

type OrgMemberOption = { id: string; name: string; email: string };

function approvalEscalation(stage: { status: string; deadline?: string | null }) {
  if (stage.status !== 'PENDING' || !stage.deadline) {
    return { escalation: false, critical: false };
  }
  const d = new Date(stage.deadline).getTime();
  const now = Date.now();
  if (now <= d) return { escalation: false, critical: false };
  const hoursPast = (now - d) / 3600000;
  return { escalation: true, critical: hoursPast > 48 };
}

function formatDeadlineLabel(iso?: string | null) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    });
  } catch {
    return iso.slice(0, 10);
  }
}

function ApprovalStageStatusNode({ status }: { status: string }) {
  const base = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-2 text-sm font-semibold';
  switch (status) {
    case 'APPROVED':
      return (
        <span className={`${base} border-emerald-500/60 bg-emerald-500/15 text-emerald-500 dark:text-emerald-300`} title="Approved">
          ✓
        </span>
      );
    case 'REJECTED':
      return (
        <span className={`${base} border-rose-500/60 bg-rose-500/15 text-rose-500 dark:text-rose-300`} title="Rejected">
          ✕
        </span>
      );
    case 'SKIPPED':
      return (
        <span className={`${base} border-glass bg-glass-3 text-glass-text-3`} title="Skipped">
          −
        </span>
      );
    default:
      return <span className={`${base} border-glass bg-glass-3`} title="Pending" />;
  }
}

/** Calendar-day diff from today; PENDING milestones only use overdue / due-soon styling. */
function paymentDueInfo(pm: { status: string; dueDate?: string | null }) {
  if (pm.status !== 'PENDING' || !pm.dueDate) {
    return { overdue: false, dueSoon: false, badge: null as string | null };
  }
  const due = startOfDay(new Date(pm.dueDate));
  const today = startOfDay(new Date());
  const diffDays = Math.round((due.getTime() - today.getTime()) / 86400000);
  if (diffDays < 0) {
    return { overdue: true, dueSoon: false, badge: 'Overdue' };
  }
  if (diffDays <= 7) {
    return {
      overdue: false,
      dueSoon: true,
      badge: `Due in ${diffDays} day${diffDays === 1 ? '' : 's'}`,
    };
  }
  return { overdue: false, dueSoon: false, badge: `Due in ${diffDays} days` };
}

export default function JobOperationsTab({ jobId }: { jobId: string }) {
  const [milestones, setMilestones] = useState<any[]>([]);
  const [compliance, setCompliance] = useState<any>(null);
  const [stages, setStages] = useState<any[]>([]);
  const [payMs, setPayMs] = useState<any[]>([]);
  const [kolPay, setKolPay] = useState<any[]>([]);
  const [metricsJson, setMetricsJson] = useState('');
  const [newMilestone, setNewMilestone] = useState('');
  const [newStageName, setNewStageName] = useState('');
  const [newStageApproverId, setNewStageApproverId] = useState('');
  const [newStageDeadline, setNewStageDeadline] = useState('');
  const [newStageSortOrder, setNewStageSortOrder] = useState('0');
  const [orgMembers, setOrgMembers] = useState<OrgMemberOption[]>([]);
  const [stageComments, setStageComments] = useState<Record<string, string>>({});
  const [patchingStageId, setPatchingStageId] = useState<string | null>(null);
  const [contentRights, setContentRights] = useState<any[]>([]);
  const [invitations, setInvitations] = useState<any[]>([]);
  const [rightKolId, setRightKolId] = useState('');
  const [rightScope, setRightScope] = useState('Organic social, 12 months');
  const [rightExpires, setRightExpires] = useState('');
  const [rightTerritory, setRightTerritory] = useState('Malaysia');
  const [rightNotes, setRightNotes] = useState('');
  const [overduePayMs, setOverduePayMs] = useState<any[]>([]);
  const [remindingId, setRemindingId] = useState<string | null>(null);
  const [updatingPaymentId, setUpdatingPaymentId] = useState<string | null>(null);

  const kolOptions = useMemo(() => {
    const map = new Map<string, string>();
    invitations.forEach((inv) => {
      if (inv.kolProfile?.id) {
        map.set(inv.kolProfile.id, inv.kolProfile.name || inv.kolProfile.email || inv.kolProfile.id);
      }
    });
    return Array.from(map.entries()).map(([id, name]) => ({ id, name }));
  }, [invitations]);

  const load = async () => {
    try {
      const [m, c, s, p, pOverdue, k, pm, rights, invs, jobRes] = await Promise.all([
        api.get(`/jobs/${jobId}/milestones`).catch(() => ({ data: [] })),
        api.get(`/jobs/${jobId}/compliance-checklist`).catch(() => ({ data: null })),
        api.get(`/jobs/${jobId}/approval-stages`).catch(() => ({ data: [] })),
        api.get(`/jobs/${jobId}/payment-milestones`).catch(() => ({ data: [] })),
        api.get(`/jobs/${jobId}/payment-milestones/overdue`).catch(() => ({ data: [] })),
        api.get(`/jobs/${jobId}/kol-payments`).catch(() => ({ data: [] })),
        api.get(`/jobs/${jobId}/performance-metrics`).catch(() => ({ data: {} })),
        api.get(`/jobs/${jobId}/content-rights`).catch(() => ({ data: [] })),
        api.get('/invitations', { params: { jobId } }).catch(() => ({ data: [] })),
        api.get(`/jobs/${jobId}`).catch(() => ({ data: null })),
      ]);
      setMilestones(m.data || []);
      setCompliance(c.data);
      const stageList = s.data || [];
      setStages(stageList);
      setPayMs(p.data || []);
      setOverduePayMs(pOverdue.data || []);
      setKolPay(k.data || []);
      setMetricsJson(JSON.stringify(pm.data || {}, null, 2));
      setContentRights(rights.data || []);
      setInvitations(invs.data || []);

      const job = jobRes.data as
        | { brandOrgId?: string; managedByOrgId?: string | null }
        | null
        | undefined;
      if (job?.brandOrgId || job?.managedByOrgId) {
        const orgIds = [...new Set([job.managedByOrgId, job.brandOrgId].filter(Boolean))] as string[];
        const memberLists = await Promise.all(
          orgIds.map((oid) => api.get(`/organizations/${oid}/members`).catch(() => ({ data: [] })))
        );
        const byId = new Map<string, OrgMemberOption>();
        memberLists.forEach((res) => {
          (res.data || []).forEach((mem: { id: string; name?: string; email?: string }) => {
            if (!byId.has(mem.id)) {
              byId.set(mem.id, {
                id: mem.id,
                name: mem.name || mem.email || mem.id,
                email: mem.email || '',
              });
            }
          });
        });
        setOrgMembers(Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        setOrgMembers([]);
      }

      const maxOrder = stageList.length ? Math.max(...stageList.map((x: { sortOrder?: number }) => x.sortOrder ?? 0)) : -1;
      setNewStageSortOrder(String(maxOrder + 1));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    load();
  }, [jobId]);

  const saveComplianceItems = async () => {
    if (!compliance?.items) return;
    await api.put(`/jobs/${jobId}/compliance-checklist`, { items: compliance.items });
    load();
  };

  const toggleItem = (idx: number) => {
    if (!compliance?.items) return;
    const items = [...compliance.items];
    items[idx] = { ...items[idx], done: !items[idx].done };
    setCompliance({ ...compliance, items });
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Campaign milestones</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <Input value={newMilestone} onChange={(e) => setNewMilestone(e.target.value)} placeholder="Milestone title" />
            <Button
              type="button"
              onClick={async () => {
                if (!newMilestone.trim()) return;
                await api.post(`/jobs/${jobId}/milestones`, { title: newMilestone.trim() });
                setNewMilestone('');
                load();
              }}
            >
              Add
            </Button>
          </div>
          <ul className="space-y-2 text-sm">
            {milestones.map((ms) => (
              <li key={ms.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={ms.completed}
                  onChange={async () => {
                    await api.patch(`/jobs/${jobId}/milestones/${ms.id}`, { completed: !ms.completed });
                    load();
                  }}
                />
                <span className="text-glass-text">{ms.title}</span>
                {ms.dueDate && <span className="text-glass-text-3 text-xs">{ms.dueDate?.slice?.(0, 10)}</span>}
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Compliance checklist</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {compliance?.items?.map((it: any, idx: number) => (
            <label key={it.id || idx} className="flex items-center gap-2 text-sm text-glass-text">
              <input type="checkbox" className="rounded border-glass accent-accent2" checked={!!it.done} onChange={() => toggleItem(idx)} />
              {it.label}
            </label>
          ))}
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={saveComplianceItems}>
              Save checklist
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={async () => {
                await api.post(`/jobs/${jobId}/compliance-checklist/approve`);
                load();
              }}
            >
              Mark approved
            </Button>
          </div>
          {compliance?.approved && <p className="text-xs text-emerald-500 dark:text-emerald-300">Approved for broadcast workflow.</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Content usage rights</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid sm:grid-cols-2 gap-3 text-sm">
            <div>
              <label className="text-xs text-glass-text-3">KOL</label>
              <Select value={rightKolId} onChange={(e) => setRightKolId(e.target.value)}>
                <option value="">Select…</option>
                {kolOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <label className="text-xs text-glass-text-3">Scope</label>
              <Input value={rightScope} onChange={(e) => setRightScope(e.target.value)} placeholder="Usage scope" />
            </div>
            <div>
              <label className="text-xs text-glass-text-3">Expires (optional)</label>
              <Input type="date" value={rightExpires} onChange={(e) => setRightExpires(e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-glass-text-3">Territory</label>
              <Input value={rightTerritory} onChange={(e) => setRightTerritory(e.target.value)} />
            </div>
          </div>
          <Textarea
            rows={2}
            placeholder="Notes"
            value={rightNotes}
            onChange={(e) => setRightNotes(e.target.value)}
          />
          <Button
            type="button"
            size="sm"
            disabled={!rightKolId || !rightScope.trim()}
            onClick={async () => {
              await api.post(`/jobs/${jobId}/content-rights`, {
                kolProfileId: rightKolId,
                scope: rightScope.trim(),
                expiresAt: rightExpires || null,
                territory: rightTerritory || null,
                notes: rightNotes || null,
              });
              setRightNotes('');
              load();
            }}
          >
            Add right
          </Button>
          <ul className="text-sm space-y-2 border-t border-glass pt-3">
            {contentRights.map((r) => (
              <li key={r.id} className="flex justify-between gap-2 items-start">
                <div>
                  <div className="font-medium text-glass-text">{r.kolProfile?.name}</div>
                  <div className="text-glass-text-2">{r.scope}</div>
                  {r.expiresAt && (
                    <div className="text-xs text-glass-text-3">Until {r.expiresAt?.slice?.(0, 10)}</div>
                  )}
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    await api.delete(`/jobs/${jobId}/content-rights/${r.id}`);
                    load();
                  }}
                >
                  Remove
                </Button>
              </li>
            ))}
            {contentRights.length === 0 && <li className="text-glass-text-3">No rights recorded.</li>}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Approval stages</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(() => {
            const sorted = [...stages].sort(
              (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)
            );
            const approvedCount = sorted.filter((x) => x.status === 'APPROVED').length;
            const total = sorted.length;
            const anyRejected = sorted.some((x) => x.status === 'REJECTED');
            return (
              <>
                {anyRejected && (
                  <div
                    className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500 dark:text-rose-300"
                    role="alert"
                  >
                    <strong className="font-semibold">Approval blocked:</strong> at least one stage was rejected.
                    Review comments and add new stages or adjust the workflow as needed.
                  </div>
                )}
                {total > 0 && (
                  <p className="text-sm text-glass-text-2">
                    <span className="font-medium text-glass-text">{approvedCount}</span> of{' '}
                    <span className="font-medium text-glass-text">{total}</span> stages approved
                  </p>
                )}

                {sorted.length > 0 && (
                  <div
                    className="flex flex-wrap items-center gap-2 rounded-xl border border-glass bg-glass-2 px-3 py-3"
                    aria-label="Approval pipeline"
                  >
                    {sorted.map((st, idx) => (
                      <div key={st.id} className="flex items-center gap-2">
                        <div className="flex flex-col items-center gap-1">
                          <ApprovalStageStatusNode status={st.status} />
                          <span className="max-w-[5.5rem] truncate text-center text-[10px] text-glass-text-2" title={st.name}>
                            {st.name}
                          </span>
                        </div>
                        {idx < sorted.length - 1 && (
                          <div
                            className="hidden h-0.5 w-6 shrink-0 bg-glass-3 sm:block"
                            aria-hidden
                          />
                        )}
                      </div>
                    ))}
                  </div>
                )}

                <div className="rounded-xl border border-glass bg-glass-2 p-3 space-y-3">
                  <div className="text-xs font-medium uppercase tracking-[0.05em] text-glass-text-3">New stage</div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <label className="text-xs text-glass-text-3">Name</label>
                      <Input
                        value={newStageName}
                        onChange={(e) => setNewStageName(e.target.value)}
                        placeholder="e.g. Legal review"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-glass-text-3">Sort order</label>
                      <Input
                        type="number"
                        value={newStageSortOrder}
                        onChange={(e) => setNewStageSortOrder(e.target.value)}
                        placeholder="0"
                      />
                    </div>
                    <div>
                      <label className="text-xs text-glass-text-3">Approver (org member)</label>
                      <Select
                        value={newStageApproverId}
                        onChange={(e) => setNewStageApproverId(e.target.value)}
                      >
                        <option value="">Optional…</option>
                        {orgMembers.map((mem) => (
                          <option key={mem.id} value={mem.id}>
                            {mem.name}
                            {mem.email ? ` (${mem.email})` : ''}
                          </option>
                        ))}
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs text-glass-text-3">Deadline</label>
                      <Input
                        type="date"
                        value={newStageDeadline}
                        onChange={(e) => setNewStageDeadline(e.target.value)}
                      />
                    </div>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    disabled={!newStageName.trim()}
                    onClick={async () => {
                      const sortOrder = parseInt(newStageSortOrder, 10);
                      await api.post(`/jobs/${jobId}/approval-stages`, {
                        name: newStageName.trim(),
                        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 0,
                        approverUserId: newStageApproverId || undefined,
                        deadline: newStageDeadline || undefined,
                      });
                      setNewStageName('');
                      setNewStageApproverId('');
                      setNewStageDeadline('');
                      load();
                    }}
                  >
                    Add stage
                  </Button>
                  {orgMembers.length === 0 && (
                    <p className="text-xs text-amber-500 dark:text-amber-300">
                      No org members loaded (check job org access). You can still add a stage without an approver.
                    </p>
                  )}
                </div>

                <ul className="space-y-4">
                  {sorted.map((st, idx) => {
                    const esc = approvalEscalation(st);
                    const isPending = st.status === 'PENDING';
                    const deadlineMs = st.deadline ? new Date(st.deadline).getTime() : null;
                    const overduePending =
                      isPending && deadlineMs != null && !Number.isNaN(deadlineMs) && Date.now() > deadlineMs;
                    const approver = st.approver as
                      | { fullName?: string | null; email?: string | null }
                      | null
                      | undefined;
                    const approverLabel = approver
                      ? [approver.fullName, approver.email].filter(Boolean).join(' · ') || '—'
                      : 'Unassigned';
                    return (
                      <li key={st.id} className="flex gap-3">
                        <div className="flex flex-col items-center">
                          <ApprovalStageStatusNode status={st.status} />
                          {idx < sorted.length - 1 && (
                            <div className="mt-1 w-px flex-1 min-h-[12px] bg-glass-3" aria-hidden />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 space-y-2 rounded-xl border border-glass bg-glass-2 p-3">
                          <div className="flex flex-wrap items-start justify-between gap-2">
                            <div>
                              <div className="font-medium text-glass-text">{st.name}</div>
                              <div className="text-xs text-glass-text-3">Order #{st.sortOrder ?? 0}</div>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {esc.critical && (
                                <span className="rounded bg-rose-500 px-2 py-0.5 text-xs font-semibold text-white">
                                  Critical
                                </span>
                              )}
                              {esc.escalation && !esc.critical && (
                                <span className="rounded bg-rose-500/15 border border-rose-500/30 px-2 py-0.5 text-xs font-medium text-rose-500 dark:text-rose-300">
                                  Escalation needed
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-sm text-glass-text-2">
                            <span className="text-glass-text-3">Approver: </span>
                            {approverLabel}
                          </div>
                          <div
                            className={`text-sm ${overduePending ? 'font-medium text-rose-500 dark:text-rose-300' : 'text-glass-text-2'}`}
                          >
                            <span className="text-glass-text-3">Deadline: </span>
                            {formatDeadlineLabel(st.deadline)}
                            {overduePending && (
                              <span className="ml-2 text-xs font-medium text-rose-500 dark:text-rose-300">Overdue</span>
                            )}
                          </div>
                          {st.comment && (
                            <p className="text-sm text-glass-text-2 border-l-2 border-glass pl-2">
                              {st.comment}
                            </p>
                          )}
                          {isPending && (
                            <div className="space-y-2 border-t border-glass pt-3">
                              <label className="text-xs text-glass-text-3">Comment (optional)</label>
                              <Textarea
                                rows={2}
                                className="text-sm"
                                value={stageComments[st.id] ?? ''}
                                onChange={(e) =>
                                  setStageComments((prev) => ({ ...prev, [st.id]: e.target.value }))
                                }
                                placeholder="Notes for this decision…"
                              />
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  type="button"
                                  size="sm"
                                  className="bg-emerald-500 hover:brightness-110 text-white"
                                  disabled={patchingStageId === st.id}
                                  onClick={async () => {
                                    setPatchingStageId(st.id);
                                    try {
                                      await api.patch(`/jobs/${jobId}/approval-stages/${st.id}`, {
                                        status: 'APPROVED',
                                        comment: stageComments[st.id]?.trim() || undefined,
                                      });
                                      setStageComments((prev) => {
                                        const next = { ...prev };
                                        delete next[st.id];
                                        return next;
                                      });
                                      load();
                                    } catch (e: any) {
                                      alert(e.response?.data?.error || 'Approve failed');
                                    } finally {
                                      setPatchingStageId(null);
                                    }
                                  }}
                                >
                                  {patchingStageId === st.id ? 'Saving…' : 'Approve'}
                                </Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="border-rose-500/40 text-rose-500 dark:text-rose-300 hover:bg-rose-500/10"
                                  disabled={patchingStageId === st.id}
                                  onClick={async () => {
                                    setPatchingStageId(st.id);
                                    try {
                                      await api.patch(`/jobs/${jobId}/approval-stages/${st.id}`, {
                                        status: 'REJECTED',
                                        comment: stageComments[st.id]?.trim() || undefined,
                                      });
                                      setStageComments((prev) => {
                                        const next = { ...prev };
                                        delete next[st.id];
                                        return next;
                                      });
                                      load();
                                    } catch (e: any) {
                                      alert(e.response?.data?.error || 'Reject failed');
                                    } finally {
                                      setPatchingStageId(null);
                                    }
                                  }}
                                >
                                  Reject
                                </Button>
                              </div>
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {stages.length === 0 && (
                  <p className="text-sm text-glass-text-3">No approval stages yet. Add one above.</p>
                )}
              </>
            );
          })()}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Payment milestones</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {overduePayMs.length > 0 && (
            <div
              className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-sm text-rose-500 dark:text-rose-300"
              role="alert"
            >
              <strong className="font-semibold">Overdue payments:</strong>{' '}
              {overduePayMs.length} milestone{overduePayMs.length === 1 ? '' : 's'} past due date and still pending.
            </div>
          )}
          <ul className="text-sm space-y-3">
            {payMs.map((pm) => {
              const info = paymentDueInfo(pm);
              const rowClass =
                pm.status === 'PENDING' && info.overdue
                  ? 'border-rose-500/30 bg-rose-500/10'
                  : pm.status === 'PENDING' && info.dueSoon
                    ? 'border-amber-400/30 bg-amber-400/10'
                    : 'border-glass bg-glass-2';
              const titleClass =
                pm.status === 'PENDING' && info.overdue
                  ? 'text-rose-500 dark:text-rose-300'
                  : pm.status === 'PENDING' && info.dueSoon
                    ? 'text-amber-500 dark:text-amber-300'
                    : 'text-glass-text';
              return (
                <li key={pm.id} className={`rounded-xl border p-3 space-y-2 ${rowClass}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className={titleClass}>
                      <span className="font-medium">{pm.stage}</span>
                      <span className="text-glass-text-2"> — RM{pm.amountRM ?? '—'} — </span>
                      <span>{pm.status}</span>
                      {pm.dueDate && (
                        <span className="text-glass-text-3 text-xs ml-1">({pm.dueDate?.slice?.(0, 10)})</span>
                      )}
                    </div>
                    {info.badge && (
                      <span
                        className={`text-xs font-medium px-2 py-0.5 rounded ${
                          info.overdue
                            ? 'bg-rose-500/15 text-rose-500 dark:text-rose-300 border border-rose-500/30'
                            : info.dueSoon
                              ? 'bg-amber-400/15 text-amber-500 dark:text-amber-300 border border-amber-400/30'
                              : 'bg-glass-3 text-glass-text-2 border border-glass'
                        }`}
                      >
                        {info.badge}
                      </span>
                    )}
                  </div>
                  {info.overdue && pm.status === 'PENDING' && (
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={remindingId === pm.id}
                        onClick={async () => {
                          setRemindingId(pm.id);
                          try {
                            await api.post(`/jobs/${jobId}/payment-milestones/${pm.id}/remind`);
                            alert('Reminder sent');
                          } catch (e: any) {
                            alert(e.response?.data?.error || 'Reminder failed');
                          } finally {
                            setRemindingId(null);
                          }
                        }}
                      >
                        {remindingId === pm.id ? 'Sending…' : 'Send Reminder'}
                      </Button>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {payMs.length === 0 && <p className="text-sm text-glass-text-3">No payment milestones.</p>}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>KOL payments</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {kolPay.map((p) => (
            <div key={p.id} className="text-sm flex justify-between items-center border-b border-glass pb-2">
              <div>
                <span className="font-medium text-glass-text">{p.kolProfile?.name}</span>
                <span className="ml-2 text-glass-text-3">
                  RM{p.amountRM}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs px-2 py-1 rounded-full border ${
                  p.status === 'PAID' ? 'bg-emerald-500/15 text-emerald-500 dark:text-emerald-300 border-emerald-500/30' :
                  p.status === 'PENDING' ? 'bg-amber-400/15 text-amber-500 dark:text-amber-300 border-amber-400/30' :
                  p.status === 'OVERDUE' ? 'bg-rose-500/15 text-rose-500 dark:text-rose-300 border-rose-500/30' :
                  'bg-glass-3 text-glass-text-2 border-glass'
                }`}>
                  {p.status}
                </span>
                {p.status !== 'PAID' && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="text-emerald-500 dark:text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10"
                    disabled={updatingPaymentId === p.id}
                    onClick={async () => {
                      setUpdatingPaymentId(p.id);
                      try {
                        await api.patch(`/jobs/${jobId}/kol-payments/${p.id}`, { status: 'PAID' });
                        load();
                      } catch (e: any) {
                        alert(e.response?.data?.error || 'Failed to update payment');
                      } finally {
                        setUpdatingPaymentId(null);
                      }
                    }}
                  >
                    {updatingPaymentId === p.id ? 'Updating...' : 'Mark Paid'}
                  </Button>
                )}
              </div>
            </div>
          ))}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await api.get(`/jobs/${jobId}/kol-payments/export.csv`, { responseType: 'blob' });
                const url = URL.createObjectURL(new Blob([res.data], { type: 'text/csv' }));
                const a = document.createElement('a');
                a.href = url;
                a.download = 'kol-payments.csv';
                a.click();
                URL.revokeObjectURL(url);
              } catch {
                alert('Export failed');
              }
            }}
          >
            Export payments CSV
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Campaign performance (manual metrics)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Textarea
            rows={8}
            className="font-mono text-xs"
            value={metricsJson}
            onChange={(e) => setMetricsJson(e.target.value)}
          />
          <Button
            type="button"
            onClick={async () => {
              try {
                const parsed = JSON.parse(metricsJson);
                await api.put(`/jobs/${jobId}/performance-metrics`, parsed);
                alert('Saved');
              } catch {
                alert('Invalid JSON');
              }
            }}
          >
            Save metrics JSON
          </Button>
          <p className="text-xs text-glass-text-3">
            Example keys: views, likes, comments, shares, emv (numeric values).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
